type ToastApi = {
  showErrorMessage: (message: string) => void;
  showInfoMessage: (message: string) => void;
};

type FileRule = {
  signature: number[];
  requiredStrings?: string[];
  encryptMarkers?: string[];
  label: string;
};

const RULES: Record<string, FileRule> = {
  pdf: {
    signature: [0x25, 0x50, 0x44, 0x46], // "%PDF"
    requiredStrings: ['%%EOF'],
    encryptMarkers: ['/Encrypt'],
    label: 'PDF',
  },

  docx: {
    signature: [0x50, 0x4b, 0x03, 0x04], // "PK\x03\x04"
    requiredStrings: ['[Content_Types].xml', 'word/document.xml'],
    encryptMarkers: [
      '<w:documentProtection',
      'EncryptionInfo',
      'EncryptedPackage',
    ],
    label: 'Word',
  },

  doc: {
    signature: [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1],
    requiredStrings: ['WordDocument'],
    encryptMarkers: ['EncryptionInfo', 'EncryptedPackage'],
    label: 'Word',
  },

  jpg: {
    signature: [0xff, 0xd8, 0xff],
    label: 'JPG',
  },
  jpeg: {
    signature: [0xff, 0xd8, 0xff],
    label: 'JPEG',
  },
  png: {
    signature: [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a],
    label: 'PNG',
  },
};

const ACCEPTED_EXTENSIONS = Object.keys(RULES);
const MAX_SIZE_MB = 10;

export const validateFile = async (
  file: File,
  Toast: ToastApi
): Promise<boolean> => {
  try {
    const extension = file.name.split('.').pop()?.toLowerCase();

    if (!extension || !ACCEPTED_EXTENSIONS.includes(extension)) {
      Toast.showErrorMessage(
        'Please select a valid doc, docx, jpg, jpeg, png, or pdf file'
      );
      return false;
    }

    const rule = RULES[extension];

    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      Toast.showErrorMessage(`File size cannot be exceed ${MAX_SIZE_MB} MB`);
      return false;
    }

    if (file.size === 0) {
      Toast.showErrorMessage(
        `The selected ${rule.label} file could not be processed as it appears to be corrupted.`
      );
      return false;
    }

    const arrayBuffer = await file.arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);

    const signatureMatches = rule.signature.every(
      (byte, index) => bytes[index] === byte
    );

    if (!signatureMatches) {
      Toast.showErrorMessage(
        `The selected ${rule.label} file could not be processed as it appears to be corrupted.`
      );
      return false;
    }

    const content = new TextDecoder('latin1').decode(bytes);

    if (rule.encryptMarkers?.some((marker) => content.includes(marker))) {
      Toast.showErrorMessage(
        `Password-protected ${rule.label} files are not allowed`
      );
      return false;
    }

    if (rule.requiredStrings?.some((marker) => !content.includes(marker))) {
      Toast.showErrorMessage(
        `The selected ${rule.label} file could not be processed as it appears to be corrupted.`
      );
      return false;
    }

    return true;
  } catch {
    Toast.showErrorMessage(
      'The selected file could not be processed as it appears to be corrupted.'
    );
    return false;
  }
};
